import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, ChevronLeft, ChevronRight, ExternalLink, Gauge, Info, PieChart, RefreshCw, Search, Sparkles, Users, UserSearch, X,
} from 'lucide-react';
import {
  apiService, type PersonaPlayerRowDTO, type PersonasResponse, type PlayerPersonasResponse,
} from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { formatPlayerName } from '@/utils/playerName';
import { DonutChart } from './JobsCharts';
import { formatInstant } from './jobStats';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, SEARCH_DELAY_MS, pageLabel, shortUuid } from './moneyLeaderboard';
import {
  PERSONA_PERIODS, activityLabel, formatIndicatorValue, formatPercent, leadingPersona, missingByReason, personaColor, personasError,
  shareSegments, sharesSentence, sourceLabel, windowLabel,
} from './personas';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';
import ps from './PersonasSection.module.scss';

function playerLabel(row: { uuid: string; name?: string; displayName?: string }): string {
  return formatPlayerName(row.name, row.displayName, shortUuid(row.uuid));
}

/** Barre empilée des parts d'un joueur : elle fait toujours 100 %, chaque part a son nom et son % en infobulle. */
function ShareBar({ row, personas }: { row: Pick<PersonaPlayerRowDTO, 'shares'>; personas: PersonasResponse['personas'] }) {
  const segments = shareSegments(row, personas);
  return (
    <span className={ps.stack} role="img" aria-label={sharesSentence(row, personas)}>
      {segments.map((s) => (
        <span key={s.id} className={ps.segment} style={{ width: `${s.share}%`, background: s.color }} title={`${s.label} : ${s.share} %`} />
      ))}
    </span>
  );
}

/**
 * Section « Personas » de l'espace Statistiques : pour chaque joueur, un pourcentage d'appartenance à chaque persona
 * (Joueur PvP, Joueur Farmer, Mineur, Chasseur, Marchand, Pilier de faction, Joueur à sanctions), calculé par le
 * satellite à partir des autres statistiques (argent, marché, admin shop, factions, métiers, sanctions). La répartition
 * globale, la recherche d'un joueur, le tri par persona, et la fiche d'un joueur avec les indicateurs qui expliquent
 * ses parts. Face à un satellite plus ancien : « cela arrive avec le satellite 1.0.31 ».
 */
export function PersonasSection() {
  const navigate = useNavigate();
  const hasPage = useAuthStore((s) => s.hasPage);
  const gameId = useGameStore((s) => s.activeGameId);
  const groupKey = useActiveGameGroup()?.key ?? null;

  const [days, setDays] = useState<number>(30);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [persona, setPersona] = useState<string | null>(null);
  const [data, setData] = useState<PersonasResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PersonaPlayerRowDTO | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(input.trim()); setPage(0); }, SEARCH_DELAY_MS);
    return () => clearTimeout(t);
  }, [input]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const res = await apiService.getPersonas(days, page, size, query, persona);
    if (id !== requestId.current) return;
    if (res.data) { setData(res.data); setError(null); }
    else setError({ status: res.status, message: personasError(res.status, res.error) });
    setLoading(false);
  }, [days, page, size, query, persona, gameId, groupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  // Un autre projet ou groupe n'a ni les mêmes joueurs ni forcément les mêmes personas.
  useEffect(() => { setData(null); setSelected(null); setPage(0); setPersona(null); }, [gameId, groupKey]);

  const ids = useMemo(() => (data?.personas ?? []).map((p) => p.id), [data]);
  const labelOf = useMemo(() => new Map((data?.personas ?? []).map((p) => [p.id, p.label])), [data]);

  if (loading && !data && !error) return <div className={lb.state}><div className={lb.spinner} /><p>Calcul des personas...</p></div>;
  if (error && !data) {
    return (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{error.status === 404 ? 'Personas pas encore servis' : 'Personas illisibles'}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) return <div className={lb.state}><Sparkles size={32} /><h3>Section éteinte sur ce projet</h3><p>{data.reason}</p></div>;

  const leading = leadingPersona(data.personas);
  const readable = data.indicators.filter((i) => i.available).length;
  const missing = missingByReason(data.indicators);
  const searching = !!data.query;
  const sortedLabel = data.persona ? labelOf.get(data.persona) : null;
  const count = (n: number) => n.toLocaleString('fr-FR');

  const cards = [
    { icon: Users, value: count(data.population), label: 'Joueurs mesurés', sub: 'Au moins un indicateur sur la période' },
    {
      icon: PieChart, value: leading ? leading.label : '-', label: 'Persona le plus présent',
      sub: leading ? `${formatPercent(leading.averageShare)} des parts en moyenne` : 'Aucun joueur mesuré',
    },
    {
      icon: Gauge, value: `${readable} sur ${data.indicators.length}`, label: 'Indicateurs lus',
      sub: missing.length ? 'Les autres sortent du calcul' : 'Toutes les bases ont répondu',
    },
    { icon: RefreshCw, value: formatInstant(data.computedAt).split(' à ')[1] ?? '-', label: 'Calculé à', sub: 'Gardé quelques minutes par le satellite' },
  ];

  return (
    <div className={`${styles.section} ${ps.palette}`}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Pour chaque indicateur, un joueur reçoit son <strong>centile</strong> parmi ceux qui ont fait l'action : 100 s'il est
          le premier, près de 0 s'il en a fait le moins, 0 s'il ne l'a jamais faite. Un très gros joueur n'écrase donc
          personne. Le <strong>score</strong> d'un persona est la moyenne de ses indicateurs, pondérée par leurs poids ; la
          <strong> part</strong> est ce score rapporté à la somme des scores du joueur, d'où un total de 100 %. Les poids se
          règlent dans le config.yaml du satellite (bloc personas), sans code.
        </span>
      </div>

      {missing.length > 0 && (
        <div className={lb.notice}><AlertCircle size={16} />
          <span>
            {missing.map((m) => `${m.reason} : ${m.labels.join(', ')}`).join('. ')}. Ces indicateurs sortent du calcul ; les
            personas gardent les autres.
          </span>
        </div>
      )}
      {data.warnings && data.warnings.length > 0 && (
        <div className={lb.notice}><AlertCircle size={16} /><span>config.yaml : {data.warnings.join(' ; ')}.</span></div>
      )}

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}>
            <h3><PieChart size={18} /> Répartition globale</h3>
            <select className={lb.select} value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(0); }} aria-label="Période">
              {PERSONA_PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
            </select>
          </div>
          <DonutChart ariaLabel="Joueurs par persona principal" formatValue={(n) => `${count(n)} joueur${n > 1 ? 's' : ''}`}
            centerValue={count(data.population)} centerLabel="joueurs"
            slices={data.personas.map((p) => ({
              key: p.id, label: p.label, value: p.dominantPlayers, color: personaColor(p.id, ids),
              sub: `part moyenne ${formatPercent(p.averageShare)}`,
            }))} />
          <p className={styles.muted}>
            L'anneau compte les joueurs par persona principal (leur plus grande part) ; sous chaque nom, la part moyenne du persona
            sur tous les joueurs mesurés (les parts moyennes font 100 %).
          </p>
        </section>

        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Sparkles size={18} /> Personas et indicateurs</h3></div>
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead>
                <tr><th>Persona</th><th className={lb.num}>Part moyenne</th><th className={lb.num}>Principal de</th><th>Indicateurs (poids)</th></tr>
              </thead>
              <tbody>
                {data.personas.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className={ps.personaCell}>
                        <span className={styles.swatch} style={{ background: personaColor(p.id, ids) }} />
                        {p.label}{p.weight !== 1 && <span className={ps.weight}>x{p.weight.toLocaleString('fr-FR')}</span>}
                      </span>
                    </td>
                    <td className={lb.num}>{formatPercent(p.averageShare)}</td>
                    <td className={lb.num}>{count(p.dominantPlayers)}</td>
                    <td className={ps.indicatorList}>
                      {Object.entries(p.indicators).map(([id, w]) => {
                        const ind = data.indicators.find((i) => i.id === id);
                        return (
                          <span key={id} className={`${ps.indicatorPill} ${ind && !ind.available ? ps.indicatorOff : ''}`}
                            title={ind ? `${ind.detail} (${windowLabel(ind.window, data.days)}, ${sourceLabel(ind.source)})${ind.reason ? ` : ${ind.reason}` : ''}` : id}>
                            {ind?.label ?? id} <b>{w.toLocaleString('fr-FR')}</b>
                          </span>
                        );
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className={lb.toolbar}>
        <label className={lb.search}>
          <Search size={16} />
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Rechercher un pseudo, un gamertag ou un uuid"
            aria-label="Rechercher un joueur" />
          {input && <button type="button" className={lb.clear} onClick={() => setInput('')} title="Effacer la recherche"><X size={14} /></button>}
        </label>
        <select className={lb.select} value={persona ?? ''} onChange={(e) => { setPersona(e.target.value || null); setPage(0); }}
          aria-label="Trier les joueurs">
          <option value="">Trier par activité mesurée</option>
          {data.personas.map((p) => <option key={p.id} value={p.id}>Les plus {p.label}</option>)}
        </select>
        <select className={lb.select} value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} disabled={searching}
          title="Joueurs par page">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} par page</option>)}
        </select>
        <button className={lb.buttonGhost} onClick={() => void load()} disabled={loading} title="Relire les personas">
          <RefreshCw size={16} className={loading ? lb.spin : undefined} /> Actualiser
        </button>
      </div>

      {error && <div className={lb.errorBox}><AlertCircle size={16} /><span>{error.message}</span></div>}
      {data.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}

      <div className={lb.tableCard}>
        <div className={lb.tableHead}>
          <h3><UserSearch size={18} /> {searching ? `Recherche « ${data.query} »` : sortedLabel ? `Les plus « ${sortedLabel} »` : 'Joueurs'}</h3>
          <span className={lb.tableCount}>
            {searching ? `${count(data.total)} joueur${data.total > 1 ? 's' : ''} trouvé${data.total > 1 ? 's' : ''}` : pageLabel(page, data.totalPages)}
          </span>
        </div>
        {data.rows.length === 0 ? (
          <p className={lb.empty}>
            {searching
              ? 'Aucun joueur mesuré sous ce nom. La recherche porte sur le début du pseudo ou du gamertag, ou sur un uuid entier.'
              : 'Aucun joueur mesuré sur la période.'}
          </p>
        ) : (
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead>
                <tr><th>Joueur</th><th>Répartition</th><th>Persona principal</th><th className={lb.num}>Activité mesurée</th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.uuid} className={lb.row} onClick={() => setSelected(r)} title="Voir ce qui explique ses parts">
                    <td><span className={lb.playerName}>{playerLabel(r)}</span></td>
                    <td className={ps.shareCell}>
                      <ShareBar row={r} personas={data.personas} />
                      <span className={ps.sentence}>{sharesSentence(r, data.personas)}</span>
                    </td>
                    <td>
                      <span className={ps.badge} style={{ borderColor: personaColor(r.dominant, ids) }}>
                        <span className={styles.swatch} style={{ background: personaColor(r.dominant, ids) }} />
                        {labelOf.get(r.dominant) ?? r.dominant} {r.shares[r.dominant] ?? 0} %
                      </span>
                    </td>
                    <td className={lb.num} title={activityLabel(r.activity)}>
                      {r.activity.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
                      <span className={ps.activitySub}>{activityLabel(r.activity)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!searching && data.totalPages > 1 && (
          <div className={lb.pagination}>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={loading || page === 0}>
              <ChevronLeft size={16} /> Précédent
            </button>
            <span>{pageLabel(page, data.totalPages)}</span>
            <button className={lb.buttonGhost} onClick={() => setPage((p) => p + 1)} disabled={loading || page + 1 >= data.totalPages}>
              Suivant <ChevronRight size={16} />
            </button>
          </div>
        )}
        <p className={lb.hint}>
          L'activité mesurée (0 à 100) est la moyenne des scores du joueur : un joueur à 100 % d'un persona mais très peu mesuré
          n'a fait qu'une chose, et peu.
        </p>
      </div>

      {selected && (
        <PersonaPlayerModal player={selected} days={data.days} ids={ids} canOpenSheet={hasPage('players')}
          onOpenSheet={() => navigate(`/players/${selected.uuid}`)} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

/** Fiche d'un joueur : ses parts, le score de chaque persona et les indicateurs qui l'expliquent. */
function PersonaPlayerModal({ player, days, ids, canOpenSheet, onOpenSheet, onClose }: {
  player: PersonaPlayerRowDTO;
  days: number;
  ids: string[];
  canOpenSheet: boolean;
  onOpenSheet: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<PlayerPersonasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    void apiService.getPlayerPersonas(player.uuid, days).then((res) => {
      if (!live) return;
      if (res.data) { setData(res.data); setError(null); } else setError(personasError(res.status, res.error));
    });
    return () => { live = false; };
  }, [player.uuid, days]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const unitOf = new Map((data?.indicators ?? []).map((i) => [i.id, i]));
  const personas = data ? [...data.personas].sort((a, b) => b.exactShare - a.exactShare) : [];

  return (
    <div className={lb.overlay} onClick={onClose}>
      <div className={`${lb.modal} ${ps.palette}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={lb.modalHead}>
          <div>
            <h3>Personas de {playerLabel(player)}</h3>
            <span className={lb.modalSub}>
              {data ? sharesSentence({ shares: Object.fromEntries(data.personas.map((p) => [p.id, p.share])) }, data.personas) : 'Lecture...'}
              {data && `, activité mesurée ${data.activity.toLocaleString('fr-FR')} (${activityLabel(data.activity).toLowerCase()})`}
            </span>
          </div>
          <button className={lb.close} onClick={onClose} title="Fermer"><X size={18} /></button>
        </div>
        <div className={lb.modalBody}>
          {error && <div className={lb.errorBox}><AlertCircle size={16} /><span>{error}</span></div>}
          {!data && !error && <div className={lb.state}><div className={lb.spinner} /></div>}
          {data && !data.measured && <p className={lb.empty}>Aucun indicateur de ce joueur n'est positif sur la période : pas de parts.</p>}
          {data && data.measured && personas.map((p) => (
            <section key={p.id} className={ps.detail}>
              <div className={ps.detailHead}>
                <span className={ps.personaCell}><span className={styles.swatch} style={{ background: personaColor(p.id, ids) }} /><b>{p.label}</b></span>
                <span className={ps.detailNums}>part <b>{p.share} %</b>, score {p.score.toLocaleString('fr-FR')} sur 100</span>
              </div>
              <table className={lb.table}>
                <thead><tr><th>Indicateur</th><th className={lb.num}>Valeur</th><th className={lb.num}>Centile</th><th className={lb.num}>Poids</th></tr></thead>
                <tbody>
                  {p.contributions.map((c) => {
                    const ind = unitOf.get(c.indicator);
                    return (
                      <tr key={c.indicator} className={c.available ? undefined : ps.rowOff}>
                        <td title={ind ? `${ind.detail} (${windowLabel(ind.window, data.days)})` : undefined}>
                          {c.label}{!c.available && <span className={ps.activitySub}>indisponible{ind?.reason ? ` : ${ind.reason}` : ''}</span>}
                        </td>
                        <td className={lb.num}>{c.available ? formatIndicatorValue(ind?.unit ?? 'count', c.value) : '-'}</td>
                        <td className={lb.num}>{c.available ? c.centile.toLocaleString('fr-FR') : '-'}</td>
                        <td className={lb.num}>{c.weight.toLocaleString('fr-FR')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}
        </div>
        {canOpenSheet && (
          <div className={lb.modalFoot}>
            <button className={lb.buttonGhost} onClick={onOpenSheet}><ExternalLink size={16} /> Ouvrir la fiche du joueur</button>
          </div>
        )}
      </div>
    </div>
  );
}
